<template>
  <div>
    <PageDynamicBreadcrumb />

    <div class="row items-center justify-start q-mb-lg">
      <DateRangeSelector
        :initial-start-date="startDate"
        :initial-end-date="endDate"
        @update="
          dates => {
            updateDateRange(dates);
            getLeads();
          }
        "
        url-state
      />
    </div>

    <DataTable
      table-key="insurancePii"
      row-key="id"
      title="Insurance PII"
      :columns="columns"
      :rows="leads"
      :loading="loading"
      :pagination="{
        sortBy: 'id',
        descending: true,
        page: 1,
        rowsPerPage: 25
      }"
      :downloadable="true"
      :refreshable="true"
      :customizable="false"
      :filterable="true"
      :searchable="true"
      @update-rows="getLeads()"
    >
      <template v-slot:actions="row">
        <ActionsDropdownButton
          :actions="[
            {
              action: () => promptRemovePii(row),
              label: 'Delete PII'
            }
          ]"
        />
      </template>
    </DataTable>
  </div>
</template>

<script>
import PageDynamicBreadcrumb from "@/components/UI/PageDynamicBreadcrumb";
import axios from "axios";
import moment from "moment/moment";
import DataTable from "@/components/DataTable";
import DateRangeSelector from "@/components/UI/DateRangeSelector";
import ActionsDropdownButton from "@/components/UI/ActionsDropdownButton.vue";
import ConfirmationModalDelete from "@/components/UI/ConfirmationModalDelete.vue";
import {Dialog} from "quasar";


export default {
  name: "LeadDeliveryReport",
  components: {
    ActionsDropdownButton,
    DateRangeSelector,
    DataTable,
    PageDynamicBreadcrumb
  },
  data() {
    return {
      loading: true,
      startDate: this.$route.query.start_date
        ? this.$route.query.start_date
        : moment().format("YYYY-MM-DD"),
      endDate: this.$route.query.end_date
        ? this.$route.query.end_date
        : moment().format("YYYY-MM-DD"),
      datePreset: this.$route.query.date_preset
        ? this.$route.query.date_preset
        : "",
      pagination: {
        sortBy: "id",
        descending: true,
        rowsPerPage: 25
      },
      totals: {
        total: 0,
        pending: 0,
        successful: 0,
        rejected: 0
      },
      leads: [],
      pendingGetLeadsRequests: [],
      columns: [
        {
          name: "id",
          label: "PII ID",
          field: "id",
          sortable: true,
          sort: (a, b) => this.sortInteger(a, b),
          type: "dimension",
          filterable: false
        },
        {
          name: "time_created",
          label: "Time Created",
          field: row => row.time_created,
          format: val =>
            !val ? "-" : moment(val).format("MMMM D, YYYY h:mma"),
          sortable: true,
          type: "dimension",
          filterable: false
        },
        {
          name: "advertiser_account_name",
          label: "Business Account",
          field: "advertiser_account_name",
          sortable: true,
          type: "dimension",
          filterable: false
        },
        {
          name: "first_name",
          label: "First Name",
          field: "first_name",
          sortable: true,
          type: "dimension",
          filterable: false
        },
        {
          name: "last_name",
          label: "Last Name",
          field: "last_name",
          sortable: true,
          type: "dimension",
          filterable: false
        },
        {
          name: "phone",
          label: "Phone",
          field: "phone",
          sortable: true,
          type: "dimension",
          filterable: false
        },
        {
          name: "email",
          label: "Email",
          field: "email",
          sortable: true,
          type: "dimension",
          filterable: false
        },
        {
          name: "postal_code",
          label: "Postal Code",
          field: "postal_code",
          sortable: true,
          sort: (a, b) => this.sortInteger(a, b),
          type: "dimension",
          filterable: false
        },
        {
          name: "age",
          label: "Age",
          field: "age",
          sortable: true,
          type: "dimension",
          filterable: false
        },
        {
          name: "num_drivers",
          label: "Number of Drivers",
          field: "num_drivers",
          sortable: true,
          type: "dimension",
          filterable: true
        },
        {
          name: "currently_insured",
          label: "Currently Insured",
          field: "currently_insured",
          format: val => (val === "1" ? "Yes" : val === "0" ? "No" : "-"),
          sortable: true,
          type: "dimension",
          filterable: true
        },
        {
          name: "own_home",
          label: "Own Home",
          field: "own_home",
          format: val => (val === "1" ? "Yes" : val === "0" ? "No" : "-"),
          sortable: true,
          type: "dimension",
          filterable: true
        },
        {
          name: "own_multiple_vehicles",
          label: "Own Multiple Vehicles",
          field: "own_multiple_vehicles",
          format: val => (val === "1" ? "Yes" : val === "0" ? "No" : "-"),
          sortable: true,
          type: "dimension",
          filterable: true
        },
        {
          name: "bundle_home_auto",
          label: "Bundle Home Auto",
          field: "bundle_home_auto",
          format: val => (val === "1" ? "Yes" : val === "0" ? "No" : "-"),
          sortable: true,
          type: "dimension",
          filterable: true
        },
        {
          name: "num_incidents",
          label: "# of Incidents in 5 Years",
          field: "num_incidents",
          sortable: true,
          type: "dimension",
          filterable: false
        },
        {
          name: "ip_address",
          label: "IP Address",
          field: "ip_address",
          sortable: true,
          sort: (a, b) => this.sortInteger(a, b),
          type: "dimension",
          filterable: false
        },
        {
          name: "user_agent",
          label: "User Agent",
          field: "user_agent",
          sortable: true,
          type: "dimension",
          filterable: false
        },
        {
          name: "actions",
          label: "",
          field: "",
          filterable: false
        }
      ]
    };
  },
  beforeMount() {
    if (this.datePreset.length > 0) {
      let dateRange = this.getDatePresetRange(this.datePreset);
      this.startDate = dateRange.from;
      this.endDate = dateRange.to;
    }
  },
  mounted() {
    this.getLeads();
  },
  methods: {
    updateDateRange(dates = null) {
      if (dates) {
        this.startDate = dates.startDate;
        this.endDate = dates.endDate;
      }
    },
    getLeads() {
      this.loading = true;

      // Abort any existing requests
      if (this.pendingGetLeadsRequests.length > 0) {
        this.pendingGetLeadsRequests.forEach(controller => {
          controller.abort();
        });
        this.pendingGetLeadsRequests = [];
      }
      let controller = new AbortController();
      let { signal } = controller;
      this.pendingGetLeadsRequests.push(controller);

      this.leads = [];

      axios({
        url: "https://api-v2.jumpfeed.com/internal/insurance-pii-report",
        params: {
          start_date: this.startDate,
          end_date: this.endDate
        },
        method: "GET",
        signal: signal
      })
        .then(response => {
          this.leads = response.data.data.rows;
          this.totals = 0;

          this.loading = false;
        })
        .catch(err => {
          if (axios.isCancel(err)) {
            return;
          }
          this.loading = false;
        });
    },
    promptRemovePii(row) {
      Dialog.create({
        component: ConfirmationModalDelete,
        componentProps: {
          descriptor: row.first_name + " " + row.last_name,
        }
      }).onOk(dialog => {
        dialog.submitting = true;
        axios({
          url:
            "https://api-v2.jumpfeed.com/internal/insurance-pii-report/remove",
          params: {
            event_id: row.id
          },
          method: "POST"
        })
          .then(() => {
            this.triggerActionOutcomeAlert("delete", true);
            this.getLeads();
          })
          .catch(e => {
            this.triggerActionOutcomeAlert("delete", false);
            console.log("Error removing PII record:", e);
          })
          .finally(() => {
            dialog.submitting = false;
            dialog.hide();
          });
      });
    }
  }
};
</script>

<style scoped lang="scss"></style>
